import styled from 'styled-components';
import { DefaultText } from '../../../../../styles/global/css/GlobalText';
import { BorderColor, CoolGreySidely, GreySidely3, LightGreySidely, LightGreySidely3, PrimaryColor, SidelyBlack, SurfaceColor } from '../../../../../styles/global/css/Utils';

export const CARD_AVATAR_SIZE = '30px';
export const CARD_HEIGHT = '62px';

interface ContainerProps {
	paddingTop?: string
	flexDirection?: string
}

const Container = styled.div<ContainerProps>`
    width: 100%;
    padding: 0 20px;
    padding-bottom: 10px;
    padding-top: ${p => p.paddingTop ?? '0'};
    display: flex;
    flex-direction: ${p => p.flexDirection ?? 'row'};
    flex-wrap: wrap;
`;

const CardList = styled.div`
    width: 100%;
    padding: 8px 20px 10px;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
`;

interface CardProps {
	isExtend: boolean
}

const Card = styled.div<CardProps>`
    transition: background-color 0.2s;
    width: ${p => p.isExtend ? 'calc((100% - 16px) / 3)' : '100%'};
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 10px;
    border: 1px solid ${BorderColor};
    border-radius: 6px;
    padding: 6px 10px;
    min-height: ${CARD_HEIGHT};
    box-sizing: border-box;
    cursor: pointer;

    &:hover {
        background-color: ${LightGreySidely};
    }
`;

const ColorBar = styled.div<{ color: string }>`
    width: 4px;
    align-self: stretch;
    border-radius: 2px;
    background-color: ${p => p.color};
    flex-shrink: 0;
`;

interface CardImageProps {
	url: string
	width?: string
	borderRadius?: string
	backgroundSize?: string
}

const CardImage = styled.div<CardImageProps>`
    width: ${p => p.width ?? CARD_AVATAR_SIZE};
    height: ${p => p.width ?? CARD_AVATAR_SIZE};
    background-image: url('${p => p.url}');
    border-radius: ${p => p.borderRadius ?? '50%'};
    background-position: center;
    background-repeat: no-repeat;
    background-size: ${p => p.backgroundSize ?? 'cover'};
    flex-shrink: 0;
`;

const CardIcon = styled.div`
    width: ${CARD_AVATAR_SIZE};
    height: ${CARD_AVATAR_SIZE};
    border-radius: 50%;
    background-color: ${LightGreySidely3};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
`;

const CardLeft = styled.div`
    display: flex;
    align-items: center;
    flex-shrink: 0;
`;

const CardMiddle = styled.div`
    flex: 1;
    min-width: 0;
`;

const CardBody = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const CardActions = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
`;

const CardTitle = styled.p`
    ${DefaultText}
    line-height: 16px;
    color: ${SidelyBlack};
    font-weight: 500;
    font-size: 12px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const CardText = styled.p<{ color?: string }>`
    ${DefaultText}
    line-height: 14px;
    color: ${p => p.color ?? CoolGreySidely};
    font-size: 11px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const SeeMore = styled.p`
    ${DefaultText}
    color: ${PrimaryColor};
    width: 100%;
    margin-top: 4px;
    font-size: 12px;
    text-align: center;
    cursor: pointer;
`;

const CardProbability = styled.div<{ backgroundColor: string }>`
    font-size: 10px;
    font-weight: 500;
    color: white;
    border-radius: 50%;
    width: 22px;
    height: 22px;
    background-color: ${p => p.backgroundColor};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
`;

const PhotoTile = styled.div`
    position: relative;
    width: 72px;
    height: 72px;
    border-radius: 6px;
    border: 1px solid ${BorderColor};
    overflow: hidden;
    cursor: pointer;
    flex-shrink: 0;

    & > img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
`;

const PhotoTileActions = styled.div`
    position: absolute;
    top: 4px;
    right: 4px;
    display: flex;
    gap: 4px;
`;

const CardIconButton = styled.button`
    border: none;
    background: none;
    padding: 2px;
    margin: 0;
    display: flex;
    align-items: center;
    cursor: pointer;
    color: ${GreySidely3};
    visibility: hidden;
    transition: color 0.2s;

    ${Card}:hover &, ${PhotoTile}:hover & {
        visibility: visible;
    }

    ${PhotoTileActions} & {
        background-color: ${SurfaceColor};
        border-radius: 4px;
    }

    &:hover {
        color: ${PrimaryColor};
    }
`;

export {
	Container,
	CardList,
	Card,
	ColorBar,
	CardImage,
	CardIcon,
	CardLeft,
	CardMiddle,
	CardBody,
	CardActions,
	CardTitle,
	CardText,
	SeeMore,
	CardProbability,
	CardIconButton,
	PhotoTile,
	PhotoTileActions
};
